/** @format */

import {OauthProvider as Provider} from 'graphql/generated/types';

import {selectActivePeriod} from 'features/mediaplan-progress/helpers/selectActivePeriod';
import {toPlanPeriodInput} from 'features/mediaplan-progress/helpers/toPlanPeriodInput';

import {type AnalyticType} from 'types';
import {STATS_PROVIDERS} from 'pages/HomeGrouping/const';
import getChartIdByKpiName from 'pages/Home/DigitalCast/helpers/getChartIdByKpiName';
import {type ChartIds} from 'pages/Home/DigitalCast/models/chartIds';

import {OverviewCompany, ProjectCardModel} from '../types';
import {getProjectStatuses} from './getProjectStatuses';
import {getSensorCounts} from './getSensorCounts';

export const mapProjectCard = (company: OverviewCompany, today: string): ProjectCardModel => {
  const adSystems = company.tokens
    .filter(token => !STATS_PROVIDERS.includes(token.provider))
    .map(token => token.provider) as Provider[];

  const analytics = company.tokens
    .filter(token => STATS_PROVIDERS.includes(token.provider))
    .map(token => token.provider) as AnalyticType[];

  const {triggered, critical} = getSensorCounts(company);

  // hasMediaplan / period / kpiIds describe the plan that is ACTIVE today (covers `today`), NOT just
  // any unfinished plan: a future flight (starts after today) or a non-covering plan would otherwise
  // make the card claim a mediaplan and KPIs that the body — which keys off the active plan — can't
  // show, and would wrongly match the KPI filter. We resolve the active period, then read kpiIds from
  // that same source plan.
  const sourcePlans = company.unfinishedMediaplans.filter(plan => plan.startOn);
  const active = selectActivePeriod({plans: sourcePlans.map(toPlanPeriodInput), today});
  const period = active?.period ?? null;

  const activePlan = active
    ? sourcePlans.find(plan => {
        const input = toPlanPeriodInput(plan);
        return (
          input.startOn === active.plan.startOn &&
          input.endOn === active.plan.endOn &&
          input.repeat === active.plan.repeat
        );
      })
    : undefined;

  const hasMediaplan = activePlan != null;
  const kpiIds = [
    ...new Set(
      (activePlan?.kpis ?? [])
        .map(kpi => {
          try {
            return getChartIdByKpiName(kpi.name);
          } catch {
            return null;
          }
        })
        .filter((id): id is ChartIds => id != null),
    ),
  ];

  return {
    companyId: company.id,
    uuid: company.uuid,
    name: company.name,
    mediaplanType: company.mediaplanType,
    adSystems,
    analytics,
    statuses: getProjectStatuses(company),
    triggered,
    critical,
    hasMediaplan,
    kpiIds,
    period,
  };
};
